import { useTranslation } from 'react-i18next'

import { Body1, Body2, Caption1 } from '@ui/Typography'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'

import { getCurrencySymbol } from '@utils/transaction.utils'

import type { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

import { QuantityInput, QuantityValidationError } from '../../components/QuantityInput'

import styles from '../Widget.module.scss'

export type OrderUnit = 'units' | 'lots'

interface OrderSizeSectionProps {
  qty: number
  onQtyChange: (nextQty: number) => void
  onQtyValidationError?: (error: QuantityValidationError | null) => void
  unit: OrderUnit
  onUnitChange: (nextUnit: OrderUnit) => void
  minOrderSize: number
  maxOrderSize?: number
  minOrderSizeIncrement?: number
  marginValue?: string
  accountCurrency?: CurrencyType
  marginErrorText?: string
  showMarginRequired?: boolean
}

export const OrderSizeSection = ({
  qty,
  onQtyChange,
  onQtyValidationError,
  unit,
  onUnitChange,
  minOrderSize,
  maxOrderSize,
  minOrderSizeIncrement,
  marginValue,
  accountCurrency,
  marginErrorText,
  showMarginRequired,
}: OrderSizeSectionProps) => {
  const { t } = useTranslation()

  const unitSwitcher = (
    <Box className={styles.segmentControl}>
      <SegmentedControl
        size='md'
        variant='default'
        activeTab={unit}
        tabs={[
          { id: 'units', name: t('OrderWidget.Units'), onClick: () => onUnitChange('units') },
          { id: 'lots', name: t('OrderWidget.Lots'), onClick: () => onUnitChange('lots') },
        ]}
      />
    </Box>
  )

  return (
    <Box flex direction='column' gap='x16'>
      <Body1 medium color='text-high-em'>
        {t('OrderWidget.Size')}
      </Body1>
      <Box flex direction='column' gap='x12'>
        <QuantityInput
          key={unit}
          value={qty}
          onChange={onQtyChange}
          onValidationError={onQtyValidationError}
          min={minOrderSize}
          max={maxOrderSize}
          step={minOrderSizeIncrement}
          disabled={!minOrderSizeIncrement}
          rightContent={unitSwitcher}
        />

        {showMarginRequired && (
          <Box flex direction='column'>
            <Box flex direction='row' gap='x4'>
              <Body2 color='text-med-em'>{t('OrderWidget.Margin required')}:</Body2>
              <Body2 color='text-high-em'>
                {accountCurrency
                  ? `${getCurrencySymbol(accountCurrency)}${marginValue}`
                  : marginValue}
              </Body2>
            </Box>
            {marginErrorText && <Caption1 color='text-danger-high-em'>{marginErrorText}</Caption1>}
          </Box>
        )}
      </Box>
    </Box>
  )
}
